import {useEffect, useState} from "react";
import AxiosFoundationAPI from "./AxiosFoundationAPI";

// Which roles grant a privilege, and who holds them. Read-only provenance for the Permissions tab:
// it answers "who can do this, and how did they get it" so an administrator knows where to change it.
export interface AssignedUser {
    USID: string;
    Name: string;
    Email?: string;
}

export interface PrivilegeRole {
    RoleUSID: string;
    RoleName: string;
    // "Direct" (a ROLE_PRIVILEGE grant) or "Capability" (via a bundle). CapabilityName names the
    // bundle when it is the latter -- that is the thing an administrator edits to change access.
    GrantPath: string;
    CapabilityName: string | null;
    AssignedUsers: AssignedUser[];
}

export interface PrivilegeRoles {
    Name: string;
    Code: string | null;
    Roles: PrivilegeRole[];
}

// Resolves EVERY privilege on the page in ONE request. The Permissions tab renders a block per
// privilege (nine on the rule page), and one call each would be nine round trips for a panel nobody
// interacts with. Callers pass the full list and read results out of the returned map.
export function usePrivilegeRoles(names: string[]): {rolesByPrivilege: Record<string, PrivilegeRole[]>; loaded: boolean; failed: boolean} {
    const [rolesByPrivilege, setRolesByPrivilege] = useState<Record<string, PrivilegeRole[]>>({});
    const [loaded, setLoaded] = useState(false);
    // Tracked separately from an empty result. A failed request used to degrade to {} and render as
    // "No roles grant this privilege" — a confident, wrong answer that hid a 500 for an entire
    // debugging session. "Nobody grants this" and "we could not find out" must not look alike.
    const [failed, setFailed] = useState(false);

    const key = names.join(",");

    useEffect(() => {
        if (!key) { setLoaded(true); return; }
        let cancelled = false;
        setFailed(false);
        AxiosFoundationAPI({method: "GET", url: "t1/v1/privilege_roles", params: {"Names": key}})
            .then(response => {
                if (cancelled) return;
                let data: any = response?.data ?? {};
                if (data && data.Privileges === undefined && data.body) {
                    try { data = typeof data.body === "string" ? JSON.parse(data.body) : data.body; } catch (e) { /* ignore */ }
                }
                const out: Record<string, PrivilegeRole[]> = {};
                (data.Privileges || []).forEach((p: PrivilegeRoles) => { out[p.Name] = p.Roles || []; });
                setRolesByPrivilege(out);
                setLoaded(true);
            })
            .catch(() => { if (!cancelled) { setFailed(true); setLoaded(true); } });
        return () => { cancelled = true; };
    }, [key]);

    return {rolesByPrivilege, loaded, failed};
}
